import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { Lock, MessageSquare, Pencil } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import type { NodeDoc } from '@/lib/types';
import CommentRail from '@/components/CommentRail';
import type { CommentAnchor } from '@/lib/types';
import Breadcrumbs from '@/components/Breadcrumbs';
import StatusBadge from '@/components/StatusBadge';

// Extract the HTTP status from the api client's thrown ApiError.
function statusOf(e: unknown): number | undefined {
  if (e instanceof ApiError) return e.status;
  if (e && typeof e === 'object' && typeof (e as { status?: unknown }).status === 'number') {
    return (e as { status: number }).status;
  }
  return undefined;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Arbitrary-variant classes give the rendered content sensible typography without
// depending on the @tailwindcss/typography plugin (not in the foundation deps).
const CONTENT_CLASS = [
  'max-w-none text-ink',
  '[&_p]:my-3 [&_p]:leading-7',
  '[&_h1]:mt-8 [&_h1]:mb-3 [&_h1]:text-2xl [&_h1]:font-bold',
  '[&_h2]:mt-7 [&_h2]:mb-2 [&_h2]:text-xl [&_h2]:font-semibold',
  '[&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold',
  '[&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6',
  '[&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6',
  '[&_li]:my-1',
  '[&_a]:text-accent [&_a]:underline',
  '[&_blockquote]:my-4 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-4 [&_blockquote]:text-ink-muted',
  '[&_code]:rounded [&_code]:bg-surface-2 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-sm',
  '[&_pre]:my-4 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-surface-2 [&_pre]:p-4 [&_pre]:text-sm',
  '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
  '[&_hr]:my-6 [&_hr]:border-border',
  '[&_img]:my-4 [&_img]:max-w-full [&_img]:rounded',
  '[&_strong]:font-semibold',
].join(' ');

/** A live highlight: the quote, its context, and where to float the button. */
interface PositionedSelection extends CommentAnchor {
  /** Pixels from the top of the content container to the middle of the highlight. */
  top: number;
}

export default function DocView() {
  const { id } = useParams<{ id: string }>();
  const [node, setNode] = useState<NodeDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorStatus, setErrorStatus] = useState<number | 'unknown' | null>(null);

  // --- highlight-to-comment -------------------------------------------------
  // `selection` is a live highlight with somewhere to put the button; `pendingAnchor`
  // is what survives the click and rides along with the next comment. They are separate
  // because clicking the button destroys the selection, and the quote has to outlive it.
  const contentRef = useRef<HTMLDivElement>(null);
  const [selection, setSelection] = useState<PositionedSelection | null>(null);
  const [pendingAnchor, setPendingAnchor] = useState<CommentAnchor | null>(null);

  /**
   * Capture path for TipTap pages, where the content is same-origin and we can just
   * read the selection. HTML pages cannot use this — their content is inside an
   * opaque-origin iframe — and report themselves through FRAME_BRIDGE instead.
   */
  const onNativeSelect = useCallback((e: { target: EventTarget }) => {
    // The Add-comment button lives inside this container, so clicking it fires this
    // mouseup BEFORE its own click. On an HTML page the highlight is inside the iframe,
    // the app's own selection is empty, and treating that as "nothing selected" removed
    // the button before the click landed — the comment then posted with no quote.
    if ((e.target as HTMLElement).closest('[data-add-comment]')) return;
    const sel = window.getSelection();
    const host = contentRef.current;
    if (!sel || sel.isCollapsed || !sel.rangeCount || !host) return setSelection(null);

    const range = sel.getRangeAt(0);
    if (!host.contains(range.commonAncestorContainer)) return setSelection(null);

    const quote = sel.toString().trim();
    if (!quote) return setSelection(null);

    // Position in the content container's coordinate space, matching what the iframe
    // path reports, so one button placement works for both.
    const rect = range.getBoundingClientRect();
    const hostRect = host.getBoundingClientRect();
    // Text nodes only — see the note on `context` in FRAME_BRIDGE. An element
    // container's textContent is a whole neighbouring block, not context.
    const { startContainer: sc, endContainer: ec } = range;
    const prefix =
      sc.nodeType === Node.TEXT_NODE
        ? (sc.textContent ?? '').slice(Math.max(0, range.startOffset - 60), range.startOffset)
        : '';
    const suffix =
      ec.nodeType === Node.TEXT_NODE
        ? (ec.textContent ?? '').slice(range.endOffset, range.endOffset + 60)
        : '';

    setSelection({
      quote: quote.slice(0, 400),
      prefix,
      suffix,
      top: rect.top - hostRect.top + rect.height / 2,
    });
  }, []);

  // A new page must not inherit the previous page's highlight or pending quote.
  useEffect(() => {
    setSelection(null);
    setPendingAnchor(null);
  }, [id]);

  const editor = useEditor({
    editable: false,
    extensions: [StarterKit, Image],
    content: '',
  });

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setErrorStatus(null);
    setNode(null);
    api
      .getNode(id)
      .then((data) => {
        if (cancelled) return;
        setNode(data);
      })
      .catch((e) => {
        if (cancelled) return;
        setErrorStatus(statusOf(e) ?? 'unknown');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Push the loaded body into the read-only editor.
  useEffect(() => {
    if (editor && node?.body) {
      editor.commands.setContent(node.body as any);
    }
  }, [editor, node]);

  if (loading) {
    return <div className="p-8 text-ink-faint">Loading…</div>;
  }

  if (errorStatus === 403) {
    return (
      <div className="mx-auto max-w-md p-12 text-center">
        <Lock size={32} className="mx-auto mb-3 text-ink-faint" />
        <h1 className="text-lg font-semibold text-ink">No access</h1>
        <p className="mt-1 text-sm text-ink-muted">
          This page is in a section you're not a member of.
        </p>
        <Link to="/" className="mt-4 inline-block text-sm text-accent hover:underline">
          Back to home
        </Link>
      </div>
    );
  }

  if (errorStatus === 404 || (errorStatus && errorStatus !== 401)) {
    return (
      <div className="mx-auto max-w-md p-12 text-center">
        <h1 className="text-lg font-semibold text-ink">Page not found</h1>
        <p className="mt-1 text-sm text-ink-muted">
          It may have been moved, deleted, or never existed.
        </p>
        <Link to="/" className="mt-4 inline-block text-sm text-accent hover:underline">
          Back to home
        </Link>
      </div>
    );
  }

  if (!node) return null;

  return (
    // Article + rail as one centred row. The article keeps its 1080px cap (Dan's
    // preference, and every published page is written to it); the rail takes the
    // margin that was empty anyway. Below `lg` the rail hides itself entirely rather
    // than stacking - see CommentRail.
    <div className="mx-auto flex w-full max-w-[1460px] justify-center gap-6 px-4 sm:px-6">
      <article className="w-full min-w-0 max-w-[1080px] py-8">
      {id && <Breadcrumbs nodeId={id} />}

      <header className="mt-3 border-b border-border pb-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <h1 className="text-3xl font-bold tracking-tight text-ink">
              {node.title || 'Untitled'}
            </h1>
            <StatusBadge status={node.status} size="md" />
          </div>
          <Link
            to={`/doc/${node.id}/edit`}
            className="inline-flex shrink-0 items-center gap-1.5 rounded border border-border bg-surface px-3 py-1.5 text-sm font-medium text-ink-muted hover:bg-surface-2 hover:text-ink"
          >
            <Pencil size={14} />
            Edit
          </Link>
        </div>

        <p className="mt-3 text-xs text-ink-faint">
          Updated {fmtDate(node.updatedAt)}
          {node.updatedBy ? ` by ${node.updatedBy}` : ''}
        </p>
      </header>

      {/* `relative` anchors the floating Add-comment button, which is positioned in
          this container's coordinate space by both capture paths. */}
      {/* HTML pages report their highlight from inside the iframe (FRAME_BRIDGE); the
          app-side selection read is for TipTap pages only. */}
      <div
        ref={contentRef}
        className="relative mt-6"
        onMouseUp={node.contentType === 'html' ? undefined : onNativeSelect}
      >
        {node.contentType === 'html' ? (
          <HtmlFrame html={node.html ?? ''} onSelect={setSelection} />
        ) : editor ? (
          <EditorContent editor={editor} className={CONTENT_CLASS} />
        ) : (
          <div className="text-ink-faint">Rendering…</div>
        )}

        {selection && (
          <button
            type="button"
            data-add-comment
            style={{ top: selection.top, right: -16 }}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              setPendingAnchor({
                quote: selection.quote,
                prefix: selection.prefix,
                suffix: selection.suffix,
              });
              setSelection(null);
              window.getSelection()?.removeAllRanges();
            }}
            className="absolute z-10 inline-flex -translate-y-1/2 items-center gap-1 rounded-full border border-border bg-surface px-2.5 py-1 text-xs font-medium text-ink shadow-lg hover:bg-surface-2"
          >
            <MessageSquare size={12} />
            Add comment
          </button>
        )}
      </div>

      </article>

      <CommentRail
        nodeId={node.id}
        pendingAnchor={pendingAnchor}
        onClearAnchor={() => setPendingAnchor(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Renders a raw-HTML doc inside a sandboxed iframe. `allow-scripts` (without
 * `allow-same-origin`) lets self-contained artifacts run their own JS while
 * keeping them isolated from the app's origin — acceptable for this trusted
 * internal wiki. We still attempt to auto-grow to the content's height on load;
 * that only works when same-origin, so the read is guarded and falls back to a
 * generous min-height with its own scroll.
 *
 * `allow-top-navigation-by-user-activation` is what lets a page link to another page
 * (a release Overview's table of contents, say) via `target="_top"`. Without it the
 * link is swallowed by the frame and a wiki page cannot reference its own siblings.
 * Navigation still requires a real click — a page cannot redirect the app on its own.
 * Note links must be ABSOLUTE: an srcDoc frame has no base URL, so `/doc/<id>` is dead.
 */
/**
 * Injected into every HTML page so the frame can REPORT two things to the app: its
 * height, and what the reader has highlighted.
 *
 * The old auto-grow read `frame.contentDocument` — which is always null here, because a
 * sandbox without `allow-same-origin` is an opaque origin. So the read always threw, the
 * catch always fired, and EVERY html page was silently pinned to the 78vh fallback and
 * scrolled inside a 600px box. postMessage crosses that boundary without granting
 * same-origin (which, combined with allow-scripts, would let a page reach the app's
 * session — not a trade worth making to size a frame).
 *
 * Selection reporting has the same constraint and the same answer. The app cannot read
 * `getSelection()` inside an opaque-origin frame, so highlight-to-comment would work on
 * TipTap pages and silently do nothing on every HTML page — which is most of the wiki,
 * and all of the release documents people actually comment on. The frame reports its own
 * selection instead: the quote, a little context either side for re-finding it later,
 * and the highlight's position so the app can float a button beside it.
 *
 * `top` is measured from the top of the frame's DOCUMENT, not its viewport, and that
 * matters: the frame is auto-grown to full content height and never scrolls internally,
 * so document coordinates are exactly what the app needs to place the button.
 */
const FRAME_BRIDGE = `
<script>(function(){
  var last = 0;
  function send() {
    var d = document, h = Math.max(
      d.documentElement ? d.documentElement.scrollHeight : 0,
      d.body ? d.body.scrollHeight : 0
    );
    if (h && h !== last) { last = h; parent.postMessage({ __strWikiHeight: h }, '*'); }
  }
  addEventListener('load', send);
  addEventListener('resize', send);
  if (window.ResizeObserver && document.documentElement) {
    new ResizeObserver(send).observe(document.documentElement);
  }
  send();

  // Context is only read from TEXT nodes. A triple-click ends its range at the START of
  // the NEXT block, so endContainer is an element - and reading that element's
  // textContent scooped up a whole unrelated paragraph as the "suffix". An anchor
  // carrying somebody else's words is worse than one carrying none, because it is what
  // re-finding matches against later.
  function context(range) {
    try {
      var sc = range.startContainer, ec = range.endContainer;
      return {
        prefix: sc.nodeType === 3
          ? (sc.textContent || '').slice(Math.max(0, range.startOffset - 60), range.startOffset)
          : '',
        suffix: ec.nodeType === 3
          ? (ec.textContent || '').slice(range.endOffset, range.endOffset + 60)
          : ''
      };
    } catch (e) { return { prefix: '', suffix: '' }; }
  }

  function report() {
    var sel = window.getSelection();
    if (!sel || sel.isCollapsed || !sel.rangeCount) {
      parent.postMessage({ __strWikiSelection: null }, '*');
      return;
    }
    var quote = String(sel.toString()).trim();
    if (!quote) { parent.postMessage({ __strWikiSelection: null }, '*'); return; }
    var range = sel.getRangeAt(0);
    var r = range.getBoundingClientRect();
    var c = context(range);
    parent.postMessage({ __strWikiSelection: {
      quote: quote.slice(0, 400),
      prefix: c.prefix,
      suffix: c.suffix,
      top: Math.round(r.top + (window.scrollY || 0)),
      height: Math.round(r.height)
    } }, '*');
  }

  // mouseup covers drag-select; keyup covers shift+arrow. selectionchange alone fires
  // mid-drag on every pixel, which would strobe the button across the page.
  addEventListener('mouseup', function(){ setTimeout(report, 0); });
  addEventListener('keyup', function(e){ if (e.shiftKey || e.key === 'Escape') setTimeout(report, 0); });
})();<\/script>`;

/** Sanity bound: a page reports its own height, so don't let a bad number blow up the DOM. */
const MAX_FRAME_PX = 30000;

function HtmlFrame({
  html,
  onSelect,
}: {
  html: string;
  onSelect: (s: PositionedSelection | null) => void;
}) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState<number | null>(null);

  // Kept in a ref so the message listener can stay mounted once, rather than being
  // torn down and re-attached every time the parent re-renders a new closure.
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    function onMessage(e: MessageEvent) {
      // Only trust the frame we actually rendered — any page on the internet can postMessage.
      if (!ref.current || e.source !== ref.current.contentWindow) return;
      const data = e.data as {
        __strWikiHeight?: unknown;
        __strWikiSelection?: unknown;
      } | null;
      if (!data) return;

      const h = data.__strWikiHeight;
      if (typeof h === 'number' && h > 0) setHeight(Math.min(Math.ceil(h), MAX_FRAME_PX));

      if ('__strWikiSelection' in data) {
        const sel = data.__strWikiSelection as Record<string, unknown> | null;
        if (!sel || typeof sel.quote !== 'string' || !sel.quote.trim()) {
          onSelectRef.current(null);
          return;
        }
        // The frame reports document coordinates and never scrolls internally, so its
        // top is already this container's top — the iframe starts at y=0 inside it.
        const top = typeof sel.top === 'number' ? sel.top : 0;
        const hgt = typeof sel.height === 'number' ? sel.height : 0;
        onSelectRef.current({
          quote: String(sel.quote).slice(0, 400),
          prefix: typeof sel.prefix === 'string' ? sel.prefix : '',
          suffix: typeof sel.suffix === 'string' ? sel.suffix : '',
          top: top + hgt / 2,
        });
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Reset when navigating to another doc, or the new page inherits the old one's height.
  useEffect(() => {
    setHeight(null);
  }, [html]);

  return (
    <iframe
      ref={ref}
      srcDoc={html + FRAME_BRIDGE}
      title="HTML content"
      sandbox="allow-scripts allow-popups allow-forms allow-modals allow-top-navigation-by-user-activation"
      /* No border / rounded / card: a self-contained HTML doc should read as its OWN full page,
         not a boxed embed you scroll inside. The height reporter auto-grows the frame to the
         content's full height, so the page scrolls with the app pane (no inner scrollbar) and the
         doc's own background bleeds seamlessly into the view. The sandboxed iframe itself is a
         hard security boundary (allow-scripts without allow-same-origin) and cannot be removed —
         borderless + auto-grown is the closest a self-contained doc gets to a native full page. */
      className="block w-full border-0"
      /* NOT bg-white. A page that sets no background of its own used to get white from here —
         while its own prefers-color-scheme:dark rules still supplied LIGHT text. Light text on
         a white card: invisible. Three of the release pages were exactly that. Falling back to
         the app's surface means a page that forgets a background is merely plain, never unreadable. */
      style={{
        background: 'var(--surface)',
        ...(height ? { height: `${height}px` } : { minHeight: '78vh' }),
      }}
    />
  );
}
